// ============================================================
// CalendarDayReminders.tsx
// The "Remind me" section of the calendar day-panel (Stage 2 of
// notifications). Personal reminders for ONE day — list / add / edit /
// delete, each with text + an optional time. Wired to the Reminder CRUD;
// after any mutation it calls onMutated so the parent refetches the month
// (updating the grid's 🔔 indicator). Casevo-styled, self-contained.
//
// A reminder fires a bell notification on/after its day via the daily
// cron scan — this UI just captures it.
// ============================================================
import { IcBell } from './calendarIcons'
import React, { useCallback, useEffect, useState } from 'react'
import { Reminder } from '../../types/calendar.types'
import { ErrorNote } from '../shared/ActionBox'
import { createReminderApi, updateReminderApi, deleteReminderApi } from '../../api/casevoApi'

interface CalendarDayRemindersProps {
  date: Date // the day these reminders are for (local-midnight civil day)
  reminders: Reminder[] // parent-filtered to this day
  onMutated: () => void
  // Diary right-click: when "Add reminder" is chosen from an HOUR slot, the
  // clicked time ('HH:mm', e.g. '14:00') seeds the composer's time input so
  // the reminder defaults to that hour (the user can still change/clear it).
  // null/undefined → no pre-fill (whole-day reminder).
  initialDraftTime?: string | null
}

// 'YYYY-MM-DD' civil day from LOCAL parts (matches the panel/grid keying;
// the backend normalises to UTC-midnight date-only).
const isoDay = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

// Build a remindAt ISO from the civil day + "HH:mm" (local), or null when
// no time is given (whole-day reminder).
const buildRemindAt = (day: Date, time: string): string | null => {
  if (!time) return null
  const [hh, mm] = time.split(':').map((n) => parseInt(n, 10))
  if (isNaN(hh) || isNaN(mm)) return null
  const dt = new Date(day.getFullYear(), day.getMonth(), day.getDate(), hh, mm, 0, 0)
  return dt.toISOString()
}

// "HH:mm" (local) from a remindAt ISO, for display + seeding the edit input.
const timeLabel = (remindAt: string | null): string => {
  if (!remindAt) return ''
  const d = new Date(remindAt)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
}

// The server's message (e.g. an invalid time is refused, never saved as all-day).
const serverError = (err: unknown, fallback: string): string =>
  (err as { response?: { data?: { error?: string } } })?.response?.data?.error || (err instanceof Error && err.message) || fallback

const inputCls =
  'w-full border border-casevo-border rounded px-2.5 py-1.5 text-sm bg-casevo-input text-casevo-forest placeholder:text-casevo-mid focus:outline-none focus:border-casevo-mid'

const CalendarDayReminders: React.FC<CalendarDayRemindersProps> = ({ date, reminders, onMutated, initialDraftTime }) => {
  // Local list seeded from props; kept in sync when the day/props change.
  const [localReminders, setLocalReminders] = useState<Reminder[]>(reminders)
  useEffect(() => {
    setLocalReminders(reminders)
  }, [reminders])

  const [draftText, setDraftText] = useState('')
  // Seed the composer's time from the right-click pre-fill on mount; re-seed
  // when the target day or the pre-fill changes (e.g. right-clicking a
  // different hour), so the composer defaults to the clicked hour.
  const [draftTime, setDraftTime] = useState(initialDraftTime ?? '')
  useEffect(() => {
    setDraftTime(initialDraftTime ?? '')
  }, [date, initialDraftTime])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [editingId, setEditingId] = useState<string | null>(null)
  const [editText, setEditText] = useState('')
  const [editTime, setEditTime] = useState('')

  const dayKey = isoDay(date)

  const handleAdd = useCallback(async () => {
    const text = draftText.trim()
    if (!text) return
    setSaving(true)
    setError(null)
    try {
      const created = await createReminderApi({
        date: dayKey,
        text,
        remindAt: buildRemindAt(date, draftTime),
      })
      setLocalReminders((prev) => [...prev, created])
      setDraftText('')
      setDraftTime('')
      onMutated()
    } catch (err) {
      setError(serverError(err, 'Failed to add reminder'))
    } finally {
      setSaving(false)
    }
  }, [draftText, draftTime, date, dayKey, onMutated])

  const startEdit = (r: Reminder): void => {
    setEditingId(r.id)
    setEditText(r.text)
    setEditTime(timeLabel(r.remindAt))
    setError(null)
  }
  const cancelEdit = (): void => {
    setEditingId(null)
    setEditText('')
    setEditTime('')
  }
  const handleSaveEdit = async (r: Reminder): Promise<void> => {
    const text = editText.trim()
    if (!text) return
    setSaving(true)
    setError(null)
    try {
      const updated = await updateReminderApi(r.id, {
        text,
        remindAt: buildRemindAt(date, editTime),
      })
      setLocalReminders((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))
      setEditingId(null)
      onMutated()
    } catch (err) {
      setError(serverError(err, 'Failed to update reminder'))
    } finally {
      setSaving(false)
    }
  }
  const handleDelete = async (r: Reminder): Promise<void> => {
    setSaving(true)
    setError(null)
    try {
      await deleteReminderApi(r.id)
      setLocalReminders((prev) => prev.filter((x) => x.id !== r.id))
      if (editingId === r.id) setEditingId(null)
      onMutated()
    } catch (err) {
      setError(serverError(err, 'Failed to delete reminder'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <section>
      <h3 className="text-xs uppercase tracking-widest text-casevo-mid mb-2">Remind me</h3>
      {error && (
        <ErrorNote className="mb-2">{error}</ErrorNote>
      )}
      {localReminders.length === 0 && (
        <p className="text-sm text-casevo-mid mb-3">No reminders for this day.</p>
      )}
      <div className="flex flex-col gap-2 mb-4">
        {localReminders.map((r) => {
          const editing = editingId === r.id
          return (
            <div key={r.id} className="rounded-lg border border-casevo-border bg-casevo-surface p-3">
              {editing ? (
                <div className="flex flex-col gap-2">
                  <textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    rows={2}
                    className={`${inputCls} resize-y`}
                  />
                  <div className="flex items-center justify-between gap-2">
                    <input
                      type="time"
                      value={editTime}
                      onChange={(e) => setEditTime(e.target.value)}
                      className="border border-casevo-border rounded px-2 py-1 text-xs bg-casevo-input text-casevo-forest focus:outline-none focus:border-casevo-mid"
                      aria-label="Reminder time (optional)"
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={cancelEdit}
                        disabled={saving}
                        className="text-xs text-casevo-mid hover:text-casevo-forest px-2 py-1 focus:outline-none casevo-focus disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => { void handleSaveEdit(r) }}
                        disabled={saving || !editText.trim()}
                        className="text-xs text-white bg-casevo-primary hover:bg-casevo-primary-hover rounded px-3 py-1 focus:outline-none casevo-focus disabled:opacity-50"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start gap-2">
                    <IcBell className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
                    <p className="text-sm text-casevo-forest whitespace-pre-wrap break-words flex-1">
                      {r.text}
                    </p>
                  </div>
                  <div className="flex items-center justify-between mt-2 gap-2">
                    <span className="text-[11px] text-casevo-mid">
                      {r.remindAt ? timeLabel(r.remindAt) : 'All day'}
                    </span>
                    <div className="flex gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => startEdit(r)}
                        className="text-[11px] text-casevo-mid hover:text-casevo-forest focus:outline-none casevo-focus"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => { void handleDelete(r) }}
                        disabled={saving}
                        className="text-[11px] text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 focus:outline-none casevo-focus disabled:opacity-50"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )
        })}
      </div>

      {/* Composer */}
      <div className="rounded-lg border border-casevo-border bg-casevo-surface p-3 flex flex-col gap-2">
        <textarea
          value={draftText}
          onChange={(e) => setDraftText(e.target.value)}
          rows={2}
          placeholder="Remind me to…"
          className={`${inputCls} resize-y`}
        />
        <div className="flex items-center justify-between gap-2">
          <input
            type="time"
            value={draftTime}
            onChange={(e) => setDraftTime(e.target.value)}
            className="border border-casevo-border rounded px-2 py-1 text-xs bg-casevo-input text-casevo-forest focus:outline-none focus:border-casevo-mid"
            aria-label="Reminder time (optional)"
          />
          <button
            type="button"
            onClick={() => { void handleAdd() }}
            disabled={saving || !draftText.trim()}
            className="text-sm text-white bg-casevo-primary hover:bg-casevo-primary-hover rounded-md px-4 py-1.5 transition-colors focus:outline-none casevo-focus disabled:opacity-50"
          >
            Add reminder
          </button>
        </div>
      </div>
    </section>
  )
}

export default CalendarDayReminders
